import { Check, ChevronsUpDown, Zap } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import { StatusIcon } from '@/components/sv/status-icon'
import { BreadcrumbItem } from '@/components/ui/breadcrumb'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useIdCompany } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  StaticSiteSwitcherQuery,
  StaticSiteSwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/static-site/static-site-switcher/switcher.types.gen'
import type { StaticSiteStatus } from '@/graphql.types'

const STATIC_SITE_SWITCHER_QUERY = gql`
  query StaticSiteSwitcher($idCompany: String!) {
    company(id: $idCompany) {
      id
      staticSites {
        id
        total
        items {
          id
          status
          name
          displayName
        }
      }
    }
  }
`

export const StaticSiteSwitcherData = () => {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { staticSiteName = '' } = useParams()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    StaticSiteSwitcherQuery,
    StaticSiteSwitcherQueryVariables
  >(STATIC_SITE_SWITCHER_QUERY, {
    variables: { idCompany },
  })

  const staticSites = data?.company?.staticSites?.items ?? []

  const activeStaticSite =
    staticSites.find((db) => db.name === staticSiteName) || staticSites[0]

  return (
    <BreadcrumbItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <SidebarMenuButton
            size="lg"
            aria-expanded={open}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div className="grid flex-1 text-left text-sm leading-tight">
              <div className="flex items-center gap-2">
                <StatusIcon
                  status={activeStaticSite?.status as StaticSiteStatus}
                />
                <span
                  title={activeStaticSite?.displayName ?? ''}
                  className="max-w-[250px] truncate text-foreground font-medium"
                >
                  {activeStaticSite?.displayName ?? ''}
                </span>
              </div>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command>
            <CommandInput
              placeholder="Search static sites..."
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>No static site found</CommandEmpty>
              <CommandGroup>
                {staticSites.map((staticSite) => (
                  <Link
                    key={staticSite.id}
                    to={`${pathname.replace(staticSiteName, staticSite.name)}?idCompany=${idCompany}`}
                    onClick={() => setOpen(false)}
                  >
                    <CommandItem
                      value={staticSite.id}
                      keywords={[staticSite.displayName]}
                      className="cursor-pointer"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border">
                        {activeStaticSite?.id === staticSite.id ? (
                          <Check className="size-3.5 shrink-0" />
                        ) : (
                          <Zap className="size-3.5 shrink-0" />
                        )}
                      </div>
                      <span
                        className={cn('truncate', {
                          'font-medium':
                            activeStaticSite?.id === staticSite?.id,
                        })}
                      >
                        {staticSite.displayName}
                      </span>
                    </CommandItem>
                  </Link>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </BreadcrumbItem>
  )
}
